import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { SnippetDeployResult } from './types';

interface Props {
  /** Eine Fassung je ZIELPORTAL — genau das, was `render-preview` liefert. `null` = zu. */
  renders: SnippetDeployResult[] | null;
  /** Auf welchem Portal soll zuerst gezeigt werden (das Bezugsportal der Fassung). */
  bevorzugtesPortal?: number | null;
  onClose: () => void;
}

/**
 * Die Schnipsel-Vorschau — mit Portal-Umschalter und einem Knopf zum Schliessen.
 *
 * ============================================================================================
 * WARUM EIN EIGENER DIALOG UND NICHT DER AUS BlocksEditor
 * ============================================================================================
 * Bei der Abnahme 2026-08-25 hatte die Vorschau NULL Bedienelemente: kein Umschalter (es stand
 * `ft` fest) und keinen Schliessen-Knopf — nur Escape, was ein Redakteur nicht raet (A40-B4).
 *
 * Die Daten waren dabei die ganze Zeit da: `render-preview` liefert seit jeher eine Fassung je
 * Zielportal, und der Adapter warf alle bis auf eine weg. Es fehlte also nichts am Server.
 *
 * ⚠ Den Vertrag von `BlocksEditor` zu erweitern waere der naheliegende Weg gewesen und der
 * falsche: dort haengt die PROFIL-Vorschau mit dran, die genau eine Fassung hat und nichts
 * umzuschalten. Ein breiterer Vertrag haette beide Seiten belastet, damit eine davon etwas kann.
 * Deshalb ein eigener Dialog plus ein Ausstieg (`vorschauExtern`) im Editor.
 */
const SnippetVorschauDialog: React.FC<Props> = ({ renders, bevorzugtesPortal, onClose }) => {
  const [aktiv, setAktiv] = useState<number | null>(null);

  useEffect(() => {
    if (!renders?.length) {
      setAktiv(null);
      return;
    }
    // ⚠ Vorbelegt mit dem BEZUGSPORTAL, nicht mit dem ersten Eintrag: bei einer portal-eigenen
    // Fassung ist das ihr eigenes Portal, und dort will der Autor zuerst hinsehen.
    const bevorzugt = renders.find((r) => Number(r.site_id) === Number(bevorzugtesPortal));
    setAktiv(Number((bevorzugt ?? renders.find((r) => r.ok) ?? renders[0]).site_id));
  }, [renders, bevorzugtesPortal]);

  const gezeigt = renders?.find((r) => Number(r.site_id) === Number(aktiv)) ?? null;

  return (
    <Dialog open={renders !== null} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Vorschau (verbindliches Server-Rendering)</DialogTitle>
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: '55vh' }}>
        {renders && renders.length > 0 && (
          <Tabs
            value={aktiv ?? false}
            onChange={(_e, v) => setAktiv(Number(v))}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            {renders.map((r) => (
              <Tab
                key={r.site_id}
                value={Number(r.site_id)}
                // ⚠ Ein Portal, dessen Render scheiterte, bekommt seinen eigenen Reiter statt aus
                // der Liste zu fallen. Sonst saehe der Autor eine heile Vorschau fuer eine Fassung,
                // die anderswo abgelehnt wird — und wuerde sie ausspielen.
                label={r.ok ? r.site_key : `${r.site_key} ⚠`}
                sx={r.ok ? undefined : { color: 'error.main' }}
              />
            ))}
          </Tabs>
        )}

        {gezeigt && !gezeigt.ok && (
          <Alert severity="error" sx={{ borderRadius: 0 }}>
            {gezeigt.site_key}: {gezeigt.error ?? 'ohne Grund'}
          </Alert>
        )}

        {/*
          ⚠ Der Dialog heisst „verbindliches Server-Rendering". Fuer die STRUKTUR stimmt das; fuer
          die TYPOGRAFIE nur, solange die gemessene Vorschau-Schicht dieses Portals aktuell ist.
          Bis 2026-08-26 war sie fuer fuenf von sechs Portalen gar nicht vorhanden, und die
          Vorschau zeigte Times — schlechter als die Realitaet, ohne das zu sagen. Wer daraus
          schliesst, sein Baustein sei kaputt, sucht an der falschen Stelle.
        */}
        {gezeigt?.ok && gezeigt.vorschauTypografie && gezeigt.vorschauTypografie !== 'ok' && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            {gezeigt.vorschauTypografie === 'fehlt'
              ? `Für ${gezeigt.site_key.toUpperCase()} ist die Vorschau-Typografie nicht hinterlegt — Schrift, Größe und Farbe unten sind Browser-Vorgaben, nicht die des Portals. Die Struktur stimmt trotzdem.`
              : `${gezeigt.site_key.toUpperCase()} hat sein Theme geändert, seit die Vorschau-Typografie gemessen wurde. Schrift, Größe oder Farbe unten können von der echten Seite abweichen.`}
          </Alert>
        )}

        {gezeigt?.ok && typeof gezeigt.html === 'string' && (
          <iframe
            title={`Schnipsel-Vorschau ${gezeigt.site_key}`}
            // ⚠ `srcDoc` je Portal NEU aufgebaut — mit einem `key` am iframe, sonst behaelt der
            // Browser beim Umschalten den alten Inhalt und der Umschalter sieht kaputt aus.
            key={gezeigt.site_id}
            // ⚠ `base href` entscheidet, wogegen RELATIVE Adressen aufloesen — ohne ihn erbt der
            // `srcdoc` die Adresse des Dashboards und Portal-Bilder brechen. Dieser Dialog hatte
            // bisher gar kein `<base>`; `target="_blank"` kommt aus derselben Ueberlegung wie in
            // der Profil-Vorschau (BlocksEditor.tsx).
            srcDoc={`<!doctype html><meta charset="utf-8">${
              gezeigt.baseUrl ? `<base href="${gezeigt.baseUrl}" target="_blank">` : ''
            }<style>${gezeigt.css ?? ''}</style>${gezeigt.html}`}
            sandbox="allow-same-origin"
            style={{ flex: 1, width: '100%', border: 0, background: '#fff', minHeight: '45vh' }}
          />
        )}

        {renders && renders.length === 0 && (
          <Alert severity="warning" sx={{ borderRadius: 0 }}>
            Für kein Portal liess sich der Schnipsel rendern.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {/* ⚠ Ein sichtbarer Schliessen-Knopf. Escape funktionierte auch vorher — aber niemand raet
            eine Tastenkombination, die nirgends steht. */}
        <Button onClick={onClose}>Schliessen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SnippetVorschauDialog;
